import groupBy from "lodash/groupBy";

/**
 * Extracts the content from parentheses in a location name.
 * Falls back to slot value if no valid parentheses content found.
 * 
 * Examples:
 *   "A01 (A)" → "A"
 *   "A01 (B)" → "B"
 *   "A01 (Left)" → "Left"
 *   "A01" → slot value (fallback)
 *   "A01 ()" → slot value (fallback)
 * 
 * @param {Object} params
 * @param {string} params.locationName - The full location name
 * @param {string} params.slot - The slot value to use as fallback
 * @returns {string} The extracted content or slot value
 */
export const extractSlotFromLocationName = ({ locationName, slot }) => {
  if (!locationName || typeof locationName !== 'string') {
    return slot || '';
  }
  
  // Match content inside the last set of parentheses
  const matches = locationName.match(/\(([^)]+)\)/g);
  
  if (matches && matches.length > 0) {
    // Get the last match and extract content
    const lastMatch = matches[matches.length - 1];
    const content = lastMatch.slice(1, -1).trim(); // Remove () and trim
    
    // Return content if non-empty, otherwise fallback to slot
    return content || slot || '';
  }
  
  // No parentheses found, use slot as fallback
  return slot || '';
};

/**
 * Removes parentheses content from location name to get base location.
 * Handles any content in parentheses, not just slot values.
 * 
 * Examples:
 *   "A01 (A)" → "A01"
 *   "A01 (Left)" → "A01"
 *   "A01 (B) (C)" → "A01 (B)" (removes last parentheses)
 * 
 * @param {Object} params
 * @param {string} params.locationName - The full location name
 * @returns {string} The location name without the slot parentheses
 */
export const removeSlotFromLocationName = ({ locationName }) => {
  if (!locationName || typeof locationName !== 'string') {
    return locationName || '';
  }
  
  // Remove the last set of parentheses and any preceding space
  return locationName.replace(/\s*\([^)]*\)\s*$/, '').trim();
};

export const getLocationGroupName = location => {
  if (location?.slotGroupKey) {
    return location.slotGroupKey;
  }

  if (location?.slot) {
    return removeSlotFromLocationName(location);
  }

  return location?.locationName || '';
};

export const getGroupedLocations = warehouseLocations => {
  return Object.entries(
    groupBy(warehouseLocations, v => [
      v.slotGroupKey || (v.slot ? removeSlotFromLocationName(v) : v.locationId),
      v.warehouseId,
    ])
  ).flatMap(([, value]) => {
    return value.map((v, index) => ({
      ...v,
      visible: index === 0,
      name: value.length > 1 ? getLocationGroupName(v) : v.locationName,
      locations: value,
    }));
  });
};

export const formatWarehouseLocations = data => {
  return (
    data?.warehouseLocationsCollection?.collection?.map(v => ({
      ...v,
      warehouseId: v.warehouse.warehouseId,
      warehouseName: v.warehouse.name,
      type: v.__typename,
    })) || []
  );
};

export const formatLocation = v => ({
  id: v.id,
  locationId: v.locationId,
  warehouseId: v.warehouseId,
  warehouseName: v.warehouseName,
  locationName: v.locationName,
  slotGroupKey: v.slotGroupKey,
  locations: v.locations,
  visible: v.visible,
  name: v.name,
  slot: v.slot,
  __typename: v.__typename,
  permanentWarehouseLocationAssignment: v.permanentWarehouseLocationAssignment,
  warehouseLocations: v.warehouseLocations,
  warehouse: v.warehouse,
});

export const getInputLabel = (number, value, warehouses) => {
  if (value.warehouse && warehouses.length > 0) {
    const warehouse = warehouses.find(w => w.warehouseId === value.warehouse?.warehouse_id);
    if (warehouse) {
      return `Storage Location ${number} within ${warehouse.name}`;
    }
  }
  return `Storage Location ${number}`;
};
